import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';
import type { Subscription, SubscriptionFilters } from '../../../types/subscriptionManagement';
import { DEPARTMENTS, STATUSES, BILLING_CYCLES, CATEGORIES } from '../../../types/subscriptionManagement';

interface Props {
  filters: SubscriptionFilters;
  onChange: (f: SubscriptionFilters) => void;
  subscriptions: Subscription[];
}

// ─── Shared dropdown trigger button ──────────────────────────────────────────
interface DropdownProps {
  label: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}
const Dropdown: React.FC<DropdownProps> = ({ label, open, onToggle, children }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onToggle();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onToggle]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border bg-white border-slate-200 text-slate-700 font-medium hover:border-slate-300 hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/40 whitespace-nowrap"
      >
        {label}
        <ChevronDown size={14} className={`text-slate-600 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg py-1 overflow-auto" style={{ minWidth: '10rem', maxHeight: '280px' }}>
          {children}
        </div>
      )}
    </div>
  );
};

// ─── Single-select dropdown filter ───────────────────────────────────────────
interface FilterDropdownProps {
  label: string;
  value: string;
  allLabel: string;
  options: string[];
  onChange: (v: string) => void;
}
const FilterDropdown: React.FC<FilterDropdownProps> = ({ label, value, allLabel, options, onChange }) => {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen(o => !o), []);

  const currentLabel = value === 'all' ? label : value;

  return (
    <Dropdown label={currentLabel} open={open} onToggle={toggle}>
      <button
        type="button"
        onClick={() => { onChange('all'); setOpen(false); }}
        className={`w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 ${value === 'all' ? 'text-blue-600 font-semibold' : 'text-slate-700'}`}
      >
        {allLabel}
      </button>
      <div className="border-t border-slate-100 my-1" />
      {options.map(opt => (
        <button
          key={opt}
          type="button"
          onClick={() => { onChange(opt); setOpen(false); }}
          className={`w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 ${value === opt ? 'text-blue-600 font-semibold' : 'text-slate-700'}`}
        >
          {opt}
        </button>
      ))}
    </Dropdown>
  );
};

// ─── Main FilterBar ───────────────────────────────────────────────────────────
export const SubscriptionFilterBar: React.FC<Props> = ({ filters, onChange }) => {
  const set = (key: keyof SubscriptionFilters, val: string) => onChange({ ...filters, [key]: val });

  const hasFilter =
    !!filters.search ||
    filters.department !== 'all' ||
    filters.status !== 'all' ||
    filters.billingCycle !== 'all' ||
    filters.category !== 'all';

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Search */}
      <div className="relative flex-1 min-w-[160px] max-w-xs">
        <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={filters.search}
          onChange={e => set('search', e.target.value)}
          placeholder="Search subscriptions…"
          className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400"
        />
        {filters.search && (
          <button
            type="button"
            onClick={() => set('search', '')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X size={13} />
          </button>
        )}
      </div>

      <div className="h-6 w-px bg-slate-200 hidden sm:block" />

      <FilterDropdown
        label="Department"
        value={filters.department}
        allLabel="All Departments"
        options={[...DEPARTMENTS]}
        onChange={v => set('department', v)}
      />

      <FilterDropdown
        label="Status"
        value={filters.status}
        allLabel="All Statuses"
        options={[...STATUSES]}
        onChange={v => set('status', v)}
      />

      <FilterDropdown
        label="Billing Cycle"
        value={filters.billingCycle}
        allLabel="All Cycles"
        options={[...BILLING_CYCLES]}
        onChange={v => set('billingCycle', v)}
      />

      <FilterDropdown
        label="Category"
        value={filters.category}
        allLabel="All Categories"
        options={[...CATEGORIES]}
        onChange={v => set('category', v)}
      />

      {hasFilter && (
        <>
          <div className="h-6 w-px bg-slate-200 hidden sm:block" />
          <button
            type="button"
            onClick={() => onChange({ search: '', department: 'all', status: 'all', billingCycle: 'all', category: 'all' })}
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-red-500 transition-colors px-2 py-1.5 rounded hover:bg-red-50"
          >
            <X size={14} /> Clear
          </button>
        </>
      )}
    </div>
  );
};
